"use client"

import { CookieNameEnum } from "@/src/constants/cookie-names"
import Category from "@/src/interfaces/catalog/category"
import Merchant from "@/src/interfaces/merchant/merchant"
import { getCookie } from "cookies-next"
import { createContext, ReactNode, useContext, useEffect, useState } from "react"
import ItemSearch from "@/src/interfaces/catalog/item-search"
import { useMerchant } from "../merchant/merchant-context"
import { useAppAccessToken } from "../app-access-token/app-access-token-context"
import { usePathname } from "next/navigation"
import CacheHandler from "@/src/core/cache/cache-handler"

interface CategoryIds {
    categoryId: string,
}

interface CategoryContextType {
    categories: Category.Category[]
    categoryIds?: CategoryIds[]
    loading: boolean
    errorMessage?: string,
    setCategoriesBySearch: (category?: ItemSearch.SearchResponse) => void
    categoriesBySearch?: ItemSearch.SearchResponse
    setSearchText: (text?: string) => void
    searchText?: string
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined)

export const CategoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { merchantSummary, localMode } = useMerchant()
    const { appAccessToken } = useAppAccessToken()
    const pathname = usePathname()

    const [categories, setCategories] = useState<Category.Category[]>([])
    const [categoriesBySearch, setCategoriesBySearch] = useState<ItemSearch.SearchResponse | undefined>(undefined)
    const [loading, setLoading] = useState<boolean>(true)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [categoryIds, setCategoryIds] = useState<CategoryIds[]>()
    const [searchText, setSearchText] = useState<string | undefined>(undefined)

    const getCategories = async () => {
        try {
            setLoading(true)

            const params: Category.SearchParams = {
                merchantId: merchantSummary?.id!,
                sellOnline: !(!!localMode)
            }

            const categories = await CacheHandler.getCategories(params, appAccessToken)

            setCategories(categories ? categories : [])
        }
        catch (e: any) {
            console.apm(e)
            setErrorMessage(e.message)
        }
        finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (typeof document !== 'undefined' && !loading) {
            const categories = document.querySelectorAll('.content-category-list')

            if (!categories) return

            const observer = new IntersectionObserver(entries => {
                const visibleSections = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

                if (visibleSections.length > 0) {
                    visibleSections.map((item) => {
                        const categoryIdObserved = item.target.getAttribute("id") ?? ""
                        let categoryId: CategoryIds = { categoryId: categoryIdObserved }
                        let currentCategoryIds = categoryIds ?? []

                        if (!currentCategoryIds.find(c => c == categoryId)) {
                            currentCategoryIds?.push(categoryId)
                            setCategoryIds(currentCategoryIds)
                        }
                    })
                }
            }, {
                threshold: 0
            })

            categories.forEach(category => observer.observe(category))

            const handleScroll = () => {
                if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
                }
            }

            window.addEventListener('scroll', handleScroll)

            return () => {
                categories.forEach(category => observer.unobserve(category))
                window.removeEventListener('scroll', handleScroll)
            }
        }
    }, [loading])

    useEffect(() => {
        if (localMode !== undefined && pathname === '/' && merchantSummary && appAccessToken)
            getCategories()
    }, [localMode, pathname, merchantSummary, appAccessToken])

    return (
        <CategoryContext.Provider value={{
            categories,
            categoriesBySearch,
            errorMessage,
            loading,
            categoryIds,
            setCategoriesBySearch,
            setSearchText,
            searchText
        }}>
            {children}
        </CategoryContext.Provider>
    )
}

export const useCategory = (): CategoryContextType => {
    const context = useContext(CategoryContext)

    if (!context) {
        throw new Error('O contexto do Category deve estar dentro do CategoryProvider.')
    }

    return context
}